import React, { useState } from "react";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";

const TERMS_I18N = {
  de: {
    pre: "Ich akzeptiere die",
    terms: "AGB",
    and: "und die",
    privacy: "Datenschutzerklärung",
    error: "Bitte AGB und Datenschutz akzeptieren.",
  },
  fr: {
    pre: "J'accepte les",
    terms: "CGV",
    and: "et la",
    privacy: "politique de confidentialité",
    error: "Veuillez accepter les CGV et la politique de confidentialité.",
  },
  it: {
    pre: "Accetto i",
    terms: "Termini",
    and: "e l'",
    privacy: "Informativa sulla privacy",
    error: "Accetta i Termini e l'informativa sulla privacy.",
  },
  en: {
    pre: "I accept the",
    terms: "Terms",
    and: "and the",
    privacy: "Privacy Policy",
    error: "Please accept the Terms and Privacy Policy.",
  },
};

export default function Register() {
  const { register, error } = useAuth();
  const { t, lang } = useLang();
  const navigate = useLocaleNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [termsErr, setTermsErr] = useState("");
  const [busy, setBusy] = useState(false);
  const ti = TERMS_I18N[lang] || TERMS_I18N.de;

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) {
      setTermsErr(ti.error);
      return;
    }
    setTermsErr("");
    setBusy(true);
    const ok = await register(name, email, password, true);
    setBusy(false);
    if (ok) navigate("/");
  };

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-4">
      <Card className="w-full border-zinc-800 bg-zinc-950 p-8 text-white">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">{t.nav.register}</p>
        <h1 className="mt-2 font-display text-3xl font-bold">{t.auth.registerTitle}</h1>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.auth.name}</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 border-zinc-800 bg-black text-white" data-testid="register-name" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.auth.email}</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 border-zinc-800 bg-black text-white" data-testid="register-email" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.auth.password}</Label>
            <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 border-zinc-800 bg-black text-white" data-testid="register-password" />
          </div>

          {/* Terms & Privacy consent — required */}
          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-zinc-800 bg-black px-3 py-2.5 text-sm leading-relaxed text-zinc-300 transition-colors hover:border-zinc-700">
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => { setTerms(e.target.checked); if (e.target.checked) setTermsErr(""); }}
              required
              data-testid="register-terms-checkbox"
              className="mt-0.5 h-4 w-4 cursor-pointer rounded border-zinc-700 bg-zinc-900 text-red-600 accent-red-600 focus:ring-1 focus:ring-red-500"
            />
            <span>
              {ti.pre} <Link to="/legal/terms" className="text-red-400 underline hover:text-red-300" data-testid="register-terms-link">{ti.terms}</Link>
              {" "}{ti.and}{" "}
              <Link to="/legal/privacy" className="text-red-400 underline hover:text-red-300" data-testid="register-privacy-link">{ti.privacy}</Link>.
            </span>
          </label>
          {termsErr && <p className="text-sm text-red-500" data-testid="register-terms-error">{termsErr}</p>}

          {error && <p className="text-sm text-red-500">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-red-600 text-white hover:bg-red-500 disabled:opacity-60" data-testid="register-submit">
            {busy ? "..." : t.auth.register}
          </Button>
        </form>
        <p className="mt-6 text-sm text-zinc-400">
          {t.auth.haveAccount} <Link to="/login" className="text-red-500 hover:underline">{t.auth.login}</Link>
        </p>
      </Card>
    </div>
  );
}
